import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Box,
  GDSStyleObject,
  HeadingText,
  Radio,
  FormField,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import COBQuestioner from '../../../../assets/json/COB_QUESTIONER.json';
import { isEmpty, compareTwoDate } from '../../../helpers/helpers';
import { Calendar } from '../../../reusable-components/calendar/calendar';
import useCobStore from '../../../../store/cob-store';
import '../../forms.css';
import { COBLayout } from '../../../cob-layout/cob-layout';

const intialValue = {
  effectiveDate: '',
  terminationDate: '',
};

export const MedicarePartB = forwardRef((props: any, ref) => {
  const { OPISResponseInfo, nextDisabled, errorHandling } = props;

  const dependantList = useCobStore((state: any) => state.demographics);
  const isBelow400 = useMediaQuery(queryHelpers.down(400));
  const isBelow380 = useMediaQuery(queryHelpers.down(380));
  const [hasMedicarePartB, setHasMedicarePartB] = useState(false);
  const [isTerminationDateSelected, setIsTerminationDateSelected] =
    useState<boolean>(false);
  const [isCheckBoxSelected, setIsChecboxSelected] = useState<boolean>(false);
  const [effectiveDateError, setEffectiveDateError] = useState<string>('');
  const [terminationDateError, setTerminationDateError] = useState<string>('');
  const [form, setForm] = useState(intialValue);
  const [errors, setErrors] = useState({
    effectiveDate: false,
    terminationDate: false,
  });

  const effectiveDate =
    form.effectiveDate || OPISResponseInfo.partBEffectiveDate;
  const terminationDate =
    form.terminationDate || OPISResponseInfo.partBCancelDate;

  const formField =
    isEmpty(effectiveDate) ||
    (isTerminationDateSelected && isEmpty(form.terminationDate)) ||
    compareTwoDate(effectiveDate, terminationDate);

  useEffect(() => {
    if (hasMedicarePartB && formField) {
      errorHandling(false);
    } else {
      errorHandling(true);
    }
  }, [errorHandling, formField, hasMedicarePartB, form]);

  useImperativeHandle(ref, () => ({
    callValidation() {
      if (isEmpty(effectiveDate)) {
        setEffectiveDateError(COBQuestioner.COB.ERRORS.REQUIREDFIELD);
        setErrors((prevState) => ({
          ...prevState,
          effectiveDate: true,
        }));
      } else {
        setEffectiveDateError('');
        setErrors((prevState) => ({
          ...prevState,
          effectiveDate: false,
        }));
      }

      if (isTerminationDateSelected || form.terminationDate) {
        if (isEmpty(terminationDate)) {
          setTerminationDateError(COBQuestioner.COB.ERRORS.VALIDDATE);
          setErrors((prevState) => ({
            ...prevState,
            terminationDate: true,
          }));
        } else if (compareTwoDate(effectiveDate, terminationDate)) {
          setTerminationDateError(COBQuestioner.COB.ERRORS.FUTUREDATE);
          setErrors((prevState) => ({
            ...prevState,
            terminationDate: true,
          }));
        } else {
          setTerminationDateError('');
          setErrors((prevState) => ({
            ...prevState,
            terminationDate: false,
          }));
        }
      }
    },
  }));

  useEffect(() => {
    if (
      hasMedicarePartB ||
      isCheckBoxSelected ||
      OPISResponseInfo.partBCoverage !== ''
    ) {
      nextDisabled(false);
    } else {
      nextDisabled(true);
    }
  }, [
    nextDisabled,
    hasMedicarePartB,
    isCheckBoxSelected,
    OPISResponseInfo.partBCoverage,
  ]);

  useEffect(() => {
    if (OPISResponseInfo.partBCoverage === 'Y') {
      setHasMedicarePartB(true);
    } else {
      setHasMedicarePartB(false);
    }
  }, [OPISResponseInfo.partBCoverage, hasMedicarePartB]);

  const updateHandler = (event: any) => {
    OPISResponseInfo.partBCoverage = event.target.value;
    if (event.target.value === 'Y') {
      setHasMedicarePartB(true);
    } else {
      setHasMedicarePartB(false);
    }
    setIsChecboxSelected(true);
    setErrors({
      effectiveDate: false,
      terminationDate: false,
    });
    OPISResponseInfo.partBEffectiveDate = '';
    OPISResponseInfo.partBCancelDate = '';
    form.effectiveDate = '';
    form.terminationDate = '';
  };

  const PartBEffectiveDate = (selectedEffectiveDate: string) => {
    setForm((prevState) => ({
      ...prevState,
      effectiveDate: selectedEffectiveDate,
    }));
    form.effectiveDate = selectedEffectiveDate;
    OPISResponseInfo.partBEffectiveDate = selectedEffectiveDate;
    setErrors((prevState) => ({
      ...prevState,
      effectiveDate: !selectedEffectiveDate,
    }));
  };

  const PartBCancelDate = (selectedTerminationDate: string) => {
    setForm((prevState) => ({
      ...prevState,
      terminationDate: selectedTerminationDate,
    }));
    form.effectiveDate = selectedTerminationDate;
    OPISResponseInfo.partBCancelDate = selectedTerminationDate;
    setErrors((prevState) => ({
      ...prevState,
      terminationDate: !selectedTerminationDate,
    }));
  };

  useEffect(() => {
    if (
      OPISResponseInfo.partBEffectiveDate ||
      OPISResponseInfo.partBCancelDate
    ) {
      form.effectiveDate = OPISResponseInfo.partBEffectiveDate;
      form.terminationDate = OPISResponseInfo.partBCancelDate;
    }
  }, [OPISResponseInfo, form]);

  return (
    <COBLayout
      title={
        OPISResponseInfo?.member?.firstName ===
          dependantList.demographics.firstName &&
        OPISResponseInfo.member.dateOfBirth ===
          dependantList?.demographics?.dateOfBirth
          ? COBQuestioner.COB.INFORMATION.YOURMEDICARE
          : `${`${OPISResponseInfo.member.firstName} ${OPISResponseInfo.member.lastName}${COBQuestioner.COB.INFORMATION.DEPENDENTMEDICARE}`}`
      }
      dataTestId="medicare_partB"
      questionTitle={
        OPISResponseInfo?.member?.firstName ===
        dependantList.demographics.firstName
          ? COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.TITLE
          : `${`${COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.DEPENDENTTITLE}${OPISResponseInfo.member.firstName}${COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.CONTINUOUSDEPENDANTTITLE}`}`
      }
      disclaimer={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.MEDICALINSURANCE}
      isDependant={false}
    >
      <Box
        css={{
          width: isBelow380 ? '332px' : '368px',
        }}
      >
        <Box className="GDS-radio-warapper marginBottom15">
          <Radio
            id="medicare_PartB_Yes"
            name="partBCoverage"
            data-testid="medicare_PartB_Yes"
            label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.ISPARTB.YES}
            onChange={(e) => {
              updateHandler(e);
            }}
            layout="row"
            checked={OPISResponseInfo?.partBCoverage === 'Y'}
            value={
              COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.ISPARTB.YES ? 'Y' : ''
            }
          />
        </Box>
        <Box className="GDS-radio-warapper marginBottom15">
          <Radio
            id="medicare_PartB_No"
            name="partBCoverage"
            data-testid="medicare_PartB_No"
            onChange={(e) => {
              updateHandler(e);
            }}
            label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.ISPARTB.NO}
            layout="row"
            checked={OPISResponseInfo?.partBCoverage === 'N'}
            value={
              COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.ISPARTB.NO ? 'N' : ''
            }
          />
        </Box>
        {hasMedicarePartB && (
          <Box>
            <HeadingText
              level="3"
              size={{
                '@initial': 'xl',
                '@mobile': 'xl',
                '@mobileLandscape': 'xl',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
              css={
                { marginTop: '$half', marginBottom: '$one' } as GDSStyleObject
              }
            >
              {' '}
              {COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.SUBTITLE}
            </HeadingText>
            <FormField
              id="effectiveDate"
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.EFFECTIVEDATE}
              aria-label={
                COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.EFFECTIVEDATE
              }
              css={
                {
                  marginBottom: '$one',
                  '& label': {
                    marginBottom: '10px',
                  },
                } as GDSStyleObject
              }
            >
              {' '}
              <Calendar
                selectedFormatDate={PartBEffectiveDate}
                currentDate={`${OPISResponseInfo?.partBEffectiveDate}`}
                showError={errors.effectiveDate}
                errorMessage={effectiveDateError}
                setWidth={isBelow400 ? '105px' : '110px'}
              />
            </FormField>
            <FormField
              id="socialSecurityDisabilityIncomeEndDate"
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.TERMINATIONDATE}
              aria-label={
                COBQuestioner.COB.QUESTIONS.CONTROLS.PARTB.TERMINATIONDATE
              }
              css={
                {
                  '& label': {
                    marginBottom: '10px',
                  },
                } as GDSStyleObject
              }
            >
              <Calendar
                selectedFormatDate={PartBCancelDate}
                currentDate={`${OPISResponseInfo?.partBCancelDate}`}
                showError={errors.terminationDate}
                errorMessage={terminationDateError}
                selectedDataValue={(val: any) =>
                  setIsTerminationDateSelected(val)
                }
                setWidth={isBelow400 ? '105px' : '110px'}
              />
            </FormField>
          </Box>
        )}
      </Box>
    </COBLayout>
  );
});
